import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  Pagination,
  TextField,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  Pets,
  Cake,
  FitnessCenter,
  LocationOn,
  Person,
  Male,
  Female,
  History,
  Scale,
  CalendarToday,
  Vaccines,
  MedicalServices,
  Agriculture,
  LocalHospital,
  ExpandMore,
  LocalDrink,
  NavigateBefore,
} from "@mui/icons-material";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";
import { apiClient, API_CONFIG } from "../../../services/api";
import { storageService } from "../../../services/storageService";
import { useDashboard } from "../../../contexts/DashboardContext";

type ReproductiveEvent = {
  event?: string;
  date?: string;
  details?: string;
};

type HandlingEvent = {
  handling_type_code: string;
  handling_date: string;
  handling_time?: string;
  title?: string;
  notes?: string;
  cost?: number | null;
  performed_by?: string;
  handling_details?: {
    peso_kg?: number;
    milk_quantity_liters?: number;
    destination_tank?: string;
    exam_type?: string;
    plate_id?: string;
    collection_date?: string;
    exam_date?: string;
    attachments?: Array<{
      type: string;
      url: string;
    }>;
    results?: {
      staphPrimary?: string;
      staphSecondary?: string;
      strepPrimary?: string;
      strepSecondary?: string;
      generalPrimary?: string;
      generalSecondary?: string;
      milkAspect?: string[];
      udderSigns?: string[];
      teatsAffected?: string[];
      observation?: string;
      result?: string;
      exam_result?: string;
    };
    [key: string]: any;
  };
};

export interface AnimalPassportData {
  // Campos básicos
  id?: string | number;
  _id?: string; // ex: "Cattle/1151958"
  _key?: string; // ex: "1151958"
  name?: string; // ex: "892"

  // Identificação do gado
  cattle_id?: {
    tag?: string; // ex: "076999093094392"
    country_code?: string; // ex: "076"
    manufacturer_code?: number; // ex: 999
    unique_id?: string; // ex: "093094392"
  };

  // Informações básicas
  breed?: string;
  breed_code?: string; // ex: "nelore"
  gender?: string; // "Macho" | "Fêmea"

  // Datas e peso
  birth_date?: string; // ex: "2023-01-01"
  birth_weight_kg?: number; // ex: 294

  // Localização
  farm_id?: string; // ex: "farm_001"
  birth_farm_id?: string;
  current_farm_id?: string;
  current_paddock_id?: string; // ex: "pasto 1"

  // Status
  current_status?: string; // ex: "active"
  operation_status?: string; // ex: "COMPLETO"

  // Timestamps
  created_at?: string;
  updated_at?: string;

  // Campos legados (manter compatibilidade)
  age?: string;
  weight?: string;
  birthDate?: string;
  location?: string;
  owner?: string;
  lastCheckup?: string;
  status?: string;

  reproductiveHistory?: ReproductiveEvent[];
  handling_history?: HandlingEvent[];

  // Biometria do focinho (imagem em base64 e metadados)
  muzzle_biometrics?: {
    base64_image?: string;
    captured_at?: string;
    captured_by?: string;
  }[];

  // Mensagem da API
  message?: string;
  count?: number;
  resumo?: string;
  url?: string;
}

interface AnimalPassportProps {
  animalData: AnimalPassportData;
  previousContentView?: string;
}

const statusChipColor = (
  status?: string
): "default" | "success" | "warning" | "error" => {
  if (!status) return "default";
  const s = status.toLowerCase();
  if (
    s.includes("saud") ||
    s.includes("completo") ||
    s.includes("ok") ||
    s.includes("dia") ||
    s.includes("active")
  )
    return "success";
  if (s.includes("venc") || s.includes("pend") || s.includes("aten"))
    return "warning";
  if (
    s.includes("erro") ||
    s.includes("doent") ||
    s.includes("crít") ||
    s.includes("crit")
  )
    return "error";
  return "default";
};





const formatTitle = (title: string): string => {
  if (!title) return title;
  
  // Remove underscores e substitui por espaços
  const withSpaces = title.replace(/_/g, ' ');
  
  // Capitaliza a primeira letra de cada palavra
  return withSpaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Utilitários de data para evitar deslocamento de fuso horário em strings "YYYY-MM-DD"
const isDateOnlyString = (s?: string): boolean => {
  return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
};

const parseDateStrict = (s?: string): Date | null => {
  if (!s) return null;
  if (isDateOnlyString(s)) {
    // Interpreta como data local (sem deslocamento de fuso)
    return new Date(`${s}T00:00:00`);
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateStrict = (s?: string): string => {
  if (!s) return '';
  if (isDateOnlyString(s)) {
    const [y, m, d] = s.split('-');
    return `${d}/${m}/${y}`;
  }
  const d = parseDateStrict(s);
  return d ? d.toLocaleDateString() : String(s);
};

export const AnimalPassport: React.FC<AnimalPassportProps> = ({
  animalData,
  previousContentView,
}) => {
  const { t } = useTranslation('animal');
  const { restorePreviousContent } = useDashboard();
  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const normalizeI18nKey = (rawKey?: string, prefix?: string) => {
    if (!rawKey) return '';
    const key = rawKey.replace(/^animal:/, '');
    if (prefix && key.startsWith(prefix)) return key.slice(prefix.length);
    return key;
  };

  // Função para traduzir tipos de manejo
  const translateHandlingType = (handlingTypeCode: string, title?: string): string => {
    const normalizedCode = normalizeI18nKey(handlingTypeCode, 'passport.handling_types.');
    const normalizedTitle = normalizeI18nKey(title, 'passport.handling_types.');
    const key = normalizedCode || normalizedTitle;
    if (key) return t(`passport.handling_types.${key}`, { defaultValue: formatTitle(key) });
    if (title) return title;
    return formatTitle(handlingTypeCode);
  };

  // Função para valores com tradução
  const valT = (v?: string | number) => {
    if (v === null || v === undefined || v === "") return t('passport.not_defined');
    return String(v);
  };

  // Função para valores (compatibilidade)
  const val = (v: any): string =>
    v === null || v === undefined || v === "" ? t('passport.not_defined') : String(v);

  // Função para traduzir chaves
  const translateKey = (key: string): string => {
    const normalized = normalizeI18nKey(key, 'passport.field_translations.');
    return t(`passport.field_translations.${normalized}`, { defaultValue: formatTitle(normalized || key) });
  };

  // Processar histórico de manejo
  const handlingHistory = animalData?.handling_history || [];
  
  // Função para obter o peso mais recente
  const getLatestWeight = () => {
     const weighingEvents = handlingHistory
       .filter((event: HandlingEvent) => event.handling_type_code === 'pesagem')
       .sort((a: HandlingEvent, b: HandlingEvent) => {
         const db = parseDateStrict(b.handling_date)?.getTime() ?? 0;
         const da = parseDateStrict(a.handling_date)?.getTime() ?? 0;
         return db - da;
       });
     
     return weighingEvents.length > 0 ? weighingEvents[0].handling_details?.peso_kg : null;
   };

  // Função para obter ícone do tipo de manejo
  const getManagementIcon = (type: string, title?: string) => {
    const t = (type || '').toLowerCase();
    const ti = (title || '').toLowerCase();

    if (t === 'pesagem') return <Scale />;
    
    if (['registrar_producao', 'producao_leite', 'ordenha'].includes(t)) {
      return (
        <Box
          sx={{
            width: 20,
            height: 20,
            bgcolor: 'white',
            maskImage: 'url(/Leite.svg)',
            WebkitMaskImage: 'url(/Leite.svg)',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
            maskSize: 'contain',
            WebkitMaskSize: 'contain',
            maskPosition: 'center',
            WebkitMaskPosition: 'center',
          }}
        />
      );
    }

    if (['registrar_vacinacao', 'vacinacao', 'vacinação'].includes(t)) return <Vaccines />;
    
    if (['medicacao', 'medicação'].includes(t)) return <MedicalServices />;
    
    // Reprodução
    if (['reproducao', 'reprodução', 'monta_natural', 'diagnostico_gestacao', 'parto', 'inseminacao', 'inseminação'].includes(t)) {
      return (
        <Box
          sx={{
            width: 20,
            height: 20,
            bgcolor: 'white',
            maskImage: 'url(/gestores/reproducao.svg)',
            WebkitMaskImage: 'url(/gestores/reproducao.svg)',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
            maskSize: 'contain',
            WebkitMaskSize: 'contain',
            maskPosition: 'center',
            WebkitMaskPosition: 'center',
          }}
        />
      );
    }
    if (t === 'manejos' && (['cio', 'secagem'].includes(ti) || ti === 'monta_natural')) {
      return (
        <Box
          sx={{
            width: 20,
            height: 20,
            bgcolor: 'white',
            maskImage: 'url(/gestores/reproducao.svg)',
            WebkitMaskImage: 'url(/gestores/reproducao.svg)',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
            maskSize: 'contain',
            WebkitMaskSize: 'contain',
            maskPosition: 'center',
            WebkitMaskPosition: 'center',
          }}
        />
      );
    }
    
    if (['nutricao', 'nutrição', 'fornecimento_nutrientes'].includes(t)) return <Agriculture />;
    
    if (['laboratory_exam', 'exame_laboratorial'].includes(t)) return <LocalHospital />;
    
    return <LocalHospital />;
  };

  // Função para determinar gravidade do resultado clínico
  const getResultSeverity = (result: string): 'grave' | 'medio' | 'leve' => {
    if (!result) return 'leve'; // Se não há resultado, considera como leve (verde)
    
    const resultLower = result.toLowerCase();
    
    // Resultados graves (vermelho)
    if (resultLower.includes('staphylococcus aureus') || 
        resultLower.includes('positivo') ||
        resultLower.includes('grave') ||
        resultLower.includes('severo') ||
        resultLower.includes('crítico') ||
        resultLower.includes('alto') ||
        resultLower.includes('elevado') ||
        resultLower.includes('infecção') ||
        resultLower.includes('infeccao') ||
        resultLower.includes('patogênico') ||
        resultLower.includes('patogenico')) {
      return 'grave';
    }
    
    // Resultados médios (laranja)
    if (resultLower.includes('moderado') ||
        resultLower.includes('médio') ||
        resultLower.includes('medio') ||
        resultLower.includes('atenção') ||
        resultLower.includes('atencao') ||
        resultLower.includes('suspeito') ||
        resultLower.includes('intermediário') ||
        resultLower.includes('intermediario') ||
        resultLower.includes('parcial') ||
        resultLower.includes('limitrofe')) {
      return 'medio';
    }
    
    // Para todos os outros casos (incluindo negativo, normal, etc.), considera como leve (verde)
    return 'leve';
  };

  // Função para obter cor baseada na gravidade do resultado
  const getSeverityColor = (severity: 'grave' | 'medio' | 'leve') => {
    switch (severity) {
      case 'grave':
        return 'error';
      case 'medio':
        return 'warning';
      case 'leve':
        return 'success';
      default:
        return 'success'; // Fallback para verde
    }
  };

  // Função para obter cor do tipo de manejo
  const getManagementColor = (type: string, title?: string) => {
    const t = (type || '').toLowerCase();
    const ti = (title || '').toLowerCase();

    if (t === 'pesagem') return 'success.main';
    
    if (['registrar_producao', 'producao_leite', 'ordenha'].includes(t)) return DESIGN_TOKENS.colors.brand.primary;
    
    if (['registrar_vacinacao', 'vacinacao', 'vacinação'].includes(t)) return 'success.main';
    
    if (['medicacao', 'medicação'].includes(t)) return 'warning.main';
    
    // Reprodução
    if (['reproducao', 'reprodução', 'monta_natural', 'diagnostico_gestacao', 'parto', 'inseminacao', 'inseminação'].includes(t)) return 'secondary.main';
    if (t === 'manejos' && (['cio', 'secagem'].includes(ti) || ti === 'monta_natural')) return 'secondary.main';
    
    if (['nutricao', 'nutrição', 'fornecimento_nutrientes'].includes(t)) return 'primary.main';
    
    if (['laboratory_exam', 'exame_laboratorial'].includes(t)) return 'primary.main'; // Usar cor padrão da aplicação
    
    return 'grey.500';
  };

  // Processar e ordenar histórico (mais recente primeiro)
  const sortedHistory = useMemo(() => {
    return [...handlingHistory].sort((a, b) => {
      const tb = parseDateStrict(b.handling_date)?.getTime() ?? 0;
      const ta = parseDateStrict(a.handling_date)?.getTime() ?? 0;
      return tb - ta;
    });
  }, [handlingHistory]);

  // Calcular paginação
  const totalPages = Math.ceil(sortedHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = sortedHistory.slice(startIndex, startIndex + itemsPerPage);
  
  const currentWeight = getLatestWeight() || animalData?.birth_weight_kg;
  
  // Biometria do focinho
  const biometricImageBase64 = animalData?.muzzle_biometrics?.[0]?.base64_image;
  const biometricCapturedAt = animalData?.muzzle_biometrics?.[0]?.captured_at;
  const biometricCapturedBy = animalData?.muzzle_biometrics?.[0]?.captured_by;
  
  // Estado e handlers para visualizar biometria em modal
  const [biometricOpen, setBiometricOpen] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [biometricUrl, setBiometricUrl] = useState<string>("");
  const [biometricError, setBiometricError] = useState<string>("");

  const sanitizeBiometricSource = (raw?: string) => {
    if (!raw) return "";
    const cleaned = raw.replace(/[`"']/g, "").trim();
    return cleaned;
  };

  const resolveEndpointFromUrl = (fullUrl: string): string | null => {
    try {
      const base = API_CONFIG.baseURL; // ex.: https://api.muu.app/api
      if (fullUrl.startsWith(base)) {
        return fullUrl.slice(base.length);
      }
      const u = new URL(fullUrl);
      const b = new URL(base);
      if (u.origin === b.origin) {
        const prefix = b.pathname.endsWith("/") ? b.pathname : b.pathname + "/";
        if (u.pathname.startsWith(prefix)) {
          return u.pathname.slice(prefix.length - 1) + u.search + u.hash;
        }
        return u.pathname + u.search + u.hash;
      }
    } catch (_) {
      // Ignora erros ao tentar normalizar a URL
    }
    return null;
  };

  const handleOpenBiometric = useCallback(async () => {
    setBiometricOpen(true);
    setBiometricLoading(true);
    setBiometricError("");

    const cleaned = sanitizeBiometricSource(biometricImageBase64);
    try {
      if (!cleaned) {
        throw new Error("Fonte de biometria ausente");
      }

      // Caso URL absoluta
      if (cleaned.startsWith("http")) {
        // Caso URL absoluta no mesmo domínio da API, busca com JWT
        const endpoint = resolveEndpointFromUrl(cleaned);
        if (endpoint) {
          const blob = await apiClient.getBlob(endpoint);
          const objectUrl = URL.createObjectURL(blob);
          setBiometricUrl(objectUrl);
          return;
        }
        // Fallback: usar URL absoluta diretamente (se pública)
        setBiometricUrl(cleaned);
        return;
      }

      // Caso seja objectKey (novo fluxo): gerar temp-access e exibir
      try {
        const temp = await storageService.getTempAccess({
          bucket: (import.meta as any)?.env?.VITE_S3_BUCKET_BIOMETRIA || "biometria",
          objectKey: cleaned,
          request: { expiresInMinutes: 300, maxAccess: 100, description: "Biometria Preview" },
        });
        if (temp?.accessUrl) {
          setBiometricUrl(temp.accessUrl);
          return;
        }
      } catch (e) {
        // Continua para fallback como base64
      }

      // Fallback: trata como base64
      setBiometricUrl(`data:image/jpeg;base64,${cleaned}`);
      return;

    } catch (err: any) {
      setBiometricError(err?.message || "Falha ao carregar biometria");
    } finally {
      setBiometricLoading(false);
    }
  }, [biometricImageBase64]);

  const handleCloseBiometric = () => {
    setBiometricOpen(false);
    setBiometricUrl("");
    setBiometricLoading(false);
    setBiometricError("");
  };
  
  const headerName =
    animalData?.name ||
    (animalData?.id !== undefined ? `${t('passport.title')} ${animalData.id}` : t('passport.title'));

  const breedDisplay = animalData?.breed || animalData?.breed_code;

  const reproductiveHistory: ReproductiveEvent[] = Array.isArray(
    animalData?.reproductiveHistory
  )
    ? animalData.reproductiveHistory
    : [];

  return (
    <Box sx={{ p: 3, height: "calc(100vh - 60px)", overflow: "auto" }}>
      {/* Header Minimalista */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <img
              src="/vaca.svg"
              alt={t('passport.animal_icon')}
              style={{
                width: 32,
                height: 32,
                color: DESIGN_TOKENS.colors.organization.farm
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: DESIGN_TOKENS.colors.organization.farm,
                  mb: 0.5
                }}
              >
                {headerName}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
              >
                {t('passport.tag')}: {animalData?.cattle_id?.tag || animalData?.name || valT()}
              </Typography>
            </Box>
            <Chip
              label={valT(
                animalData?.operation_status ||
                  animalData?.current_status ||
                  animalData?.status ||
                  animalData?.message
              )}
              color={statusChipColor(
                animalData?.operation_status ||
                  animalData?.current_status ||
                  animalData?.status
              )}
              sx={{ 
                fontWeight: 600,
                ...((
                  animalData?.operation_status?.toLowerCase() === 'completo' ||
                  animalData?.current_status?.toLowerCase() === 'completo' ||
                  animalData?.status?.toLowerCase() === 'completo'
                ) && {
                  backgroundColor: DESIGN_TOKENS.colors.brand.primary,
                  color: 'white'
                })
              }}
            />
          </Box>
          
          {/* Resumo em chips */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              icon={<img src="/vaca.svg" alt="Animal" style={{ width: 16, height: 16, color: DESIGN_TOKENS.colors.organization.farm }} />}
              label={breedDisplay || t('passport.breed_not_defined')}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            {animalData?.gender && (
              <Chip
                icon={animalData?.gender?.toLowerCase() === "macho" ? 
                  <Male sx={{ fontSize: 16 }} /> : 
                  <Female sx={{ fontSize: 16 }} />
                }
                label={animalData.gender}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Box>

        </CardContent>
      </Card>

      {/* Biometria do Focinho */}
      {biometricImageBase64 && biometricImageBase64.trim() !== '' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: DESIGN_TOKENS.colors.organization.farm,
              }}
            >
              Biometria do Focinho
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="contained" color="primary" onClick={handleOpenBiometric}>
                  Ver biometria do animal
                </Button>
                {biometricError && (
                  <Typography variant="caption" color="error">{biometricError}</Typography>
                )}
              </Box>
              {(biometricCapturedAt || biometricCapturedBy) && (
                <Box sx={{ minWidth: 240 }}>
                  {biometricCapturedAt && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CalendarToday sx={{ fontSize: 18, color: DESIGN_TOKENS.colors.text.secondary }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Capturado em: {new Date(biometricCapturedAt).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                  {biometricCapturedBy && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person sx={{ fontSize: 18, color: DESIGN_TOKENS.colors.text.secondary }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Capturado por: {biometricCapturedBy}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Informações Detalhadas */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: DESIGN_TOKENS.colors.organization.farm,
            }}
          >
            {t('passport.detailed_info')}
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            {/* Raça */}
            <Box sx={{ flex: "1 1 200px" }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <img src="/vaca.svg" alt={t('passport.animal_icon')} style={{ width: 20, height: 20, color: DESIGN_TOKENS.colors.organization.farm }} />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
                  >
                    {t('passport.fields.breed')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {valT(breedDisplay)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Sexo */}
            <Box sx={{ flex: "1 1 200px" }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                {animalData?.gender?.toLowerCase() === t('passport.gender_values.male').toLowerCase() ? (
                  <Male
                    sx={{ 
                      color: DESIGN_TOKENS.colors.organization.farm,
                      fontSize: 20
                    }}
                  />
                ) : animalData?.gender?.toLowerCase() === t('passport.gender_values.female').toLowerCase() ||
                  animalData?.gender?.toLowerCase() === "femea" ? (
                  <Female
                    sx={{ 
                      color: DESIGN_TOKENS.colors.organization.farm,
                      fontSize: 20
                    }}
                  />
                ) : (
                  <Person
                    sx={{ color: DESIGN_TOKENS.colors.organization.farm, fontSize: 20 }}
                  />
                )}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
                  >
                    {t('passport.fields.gender')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {valT(animalData?.gender)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Peso */}
            <Box sx={{ flex: "1 1 200px" }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <FitnessCenter
                  sx={{ color: DESIGN_TOKENS.colors.organization.farm, fontSize: 20 }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
                  >
                    {t('passport.fields.current_weight')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {currentWeight
                      ? `${currentWeight} kg`
                      : valT(animalData?.weight)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Localização */}
            <Box sx={{ flex: "1 1 200px" }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <LocationOn
                  sx={{ color: DESIGN_TOKENS.colors.organization.farm, fontSize: 20 }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
                  >
                    {t('passport.fields.location')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {valT(animalData?.current_paddock_id)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Nascimento */}
            <Box sx={{ flex: "1 1 200px" }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Box
                  component="img"
                  src="/Data de Nascimento.svg"
                  alt="Data de Nascimento"
                  sx={{ width: 20, height: 20 }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
                  >
                    {t('passport.fields.birth')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {animalData?.birth_date
                      ? formatDateStrict(animalData.birth_date)
                      : valT(animalData?.birthDate)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Histórico de Manejo - Timeline Vertical com Filtros e Paginação */}
      <Card sx={{ mt: 3, border: '1px solid', borderColor: 'grey.200' }}>
        <CardContent>
          {/* Header com título e resumo */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <History color="primary" />
              {t('passport.handling_history.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {sortedHistory.length} {t('passport.handling_history.records_found')}
            </Typography>
          </Box>

          {/* Timeline Vertical */}
          {sortedHistory.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {t('passport.handling_history.no_records') || 'Ainda sem registros de manejo para este animal.'}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ position: 'relative' }}>
                {(() => {
                    const displayItems = [...currentItems].reverse();
                    return displayItems.map((event, index) => (
                      <Box key={`${event.handling_date}-${index}`} sx={{ display: 'flex', mb: 3, position: 'relative' }}>
                      {/* Timeline Dot */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 3, minWidth: 40 }}>
                        <Avatar
                          sx={{ 
                            bgcolor: getManagementColor(event.handling_type_code, event.title),
                            width: 40,
                            height: 40,
                            fontSize: 20
                          }}
                        >
                          {getManagementIcon(event.handling_type_code, event.title)}
                        </Avatar>
                        {index < displayItems.length - 1 && (
                          <Box 
                            sx={{ 
                              width: 2, 
                              height: 60, 
                              bgcolor: 'grey.300',
                              mt: 1,
                            }} 
                          />
                        )}
                      </Box>
                      
                      {/* Timeline Content */}
                      <Box sx={{ flex: 1, pt: 0.5 }}>
                        <Paper 
                          elevation={1} 
                          sx={{ 
                            p: 3, 
                            bgcolor: 'background.paper',
                            border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                            borderRadius: 2,
                            '&:hover': {
                              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                              transform: 'translateY(-1px)',
                              transition: 'all 0.2s ease-in-out'
                            }
                          }}
                        >
                          <Box sx={{ mb: 2 }}>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 600, 
                                color: getManagementColor(event.handling_type_code, event.title),
                                mb: 1
                              }}
                            >
                              {event.title ? formatTitle(event.title) : translateHandlingType(event.handling_type_code)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'text.secondary',
                                  fontWeight: 500
                                }}
                              >
                                {t('passport.handling_history.handling_date')}: {formatDateStrict(event.handling_date)}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* Detalhes específicos do evento */}
                          {event.handling_type_code === 'pesagem' && event.handling_details?.peso_kg && (
                            <Box sx={{
                              mt: 2,
                              p: 2,
                              bgcolor: 'success.50',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'success.200'
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Scale sx={{ fontSize: 20, color: 'success.main' }} />
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                                  {t('passport.handling_history.weight')}: {event.handling_details.peso_kg} {t('passport.handling_history.weight_kg')}
                                </Typography>
                              </Box>
                            </Box>
                          )}

                          {/* Detalhes específicos de produção de leite / ordenha */}
                          {(event.handling_type_code === 'registrar_producao' || event.title === 'producao_leite' || event.handling_type_code === 'ordenha') &&
                           typeof event.handling_details?.milk_quantity_liters !== 'undefined' && (
                            <Box sx={{
                              mt: 2,
                              p: 2,
                              bgcolor: 'info.50',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'info.200'
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    color: DESIGN_TOKENS.colors.brand.primary,
                                    bgcolor: 'currentColor',
                                    maskImage: 'url(/Leite.svg)',
                                    WebkitMaskImage: 'url(/Leite.svg)',
                                    maskRepeat: 'no-repeat',
                                    WebkitMaskRepeat: 'no-repeat',
                                    maskSize: 'contain',
                                    WebkitMaskSize: 'contain',
                                    maskPosition: 'center',
                                    WebkitMaskPosition: 'center',
                                  }}
                                />
                                <Typography variant="body1" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.brand.primary }}>
                                  {translateKey('milk_production')}: {event.handling_details.milk_quantity_liters} L
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {event.handling_details?.destination_tank && (
                                  <Chip 
                                    label={`${translateKey('destination_tank')}: ${event.handling_details.destination_tank || t('passport.not_defined')}`}
                                    size="small" 
                                    variant="outlined"
                                    color="info"
                                  />
                                )}
                                {event.performed_by && (
                                  <Chip 
                                    label={`${translateKey('performed_by')}: ${event.performed_by}`}
                                    size="small" 
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              {event.notes && (
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                  {translateKey('notes')}: {event.notes}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {/* Detalhes específicos de sanidade: vacinação */}
                          {event.handling_type_code === 'registrar_vacinacao' && (
                            <Box sx={{
                              mt: 2,
                              p: 2,
                              bgcolor: 'success.50',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'success.200'
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Vaccines sx={{ fontSize: 20, color: 'success.main' }} />
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                                  {translateHandlingType(event.handling_type_code)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                {event.handling_details?.disease && (
                                  <Chip 
                                    label={`${translateKey('disease')}: ${val(event.handling_details.disease)}`}
                                    size="small" 
                                    variant="outlined"
                                    color="success"
                                  />
                                )}
                                {typeof event.handling_details?.dosage_ml !== 'undefined' && (
                                  <Chip 
                                    label={`${translateKey('dosage_ml')}: ${val(event.handling_details.dosage_ml)} ml`}
                                    size="small" 
                                    variant="outlined"
                                  />
                                )}
                                {event.handling_details?.product_name && (
                                  <Chip 
                                    label={`${translateKey('product_name')}: ${val(event.handling_details.product_name)}`}
                                    size="small" 
                                    variant="outlined"
                                  />
                                )}
                                {typeof event.cost !== 'undefined' && (
                                  <Chip 
                                    label={`${translateKey('cost')}: ${val(event.cost)}`}
                                    size="small" 
                                    variant="outlined"
                                  />
                                )}
                                {event.performed_by && (
                                  <Chip 
                                    label={`${translateKey('performed_by')}: ${event.performed_by}`}
                                    size="small" 
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              {event.notes && (
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                  {translateKey('notes')}: {event.notes}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {/* Detalhes específicos de nutrição: fornecimento de nutrientes */}
                          {event.handling_type_code === 'fornecimento_nutrientes' && (
                            <Box sx={{
                              mt: 2,
                              p: 2,
                              bgcolor: 'primary.50',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'primary.200'
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Agriculture sx={{ fontSize: 20, color: 'primary.main' }} />
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                  {translateHandlingType(event.handling_type_code)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                {event.handling_details?.feed_type && (
                                  <Chip 
                                    label={`${translateKey('feed_type')}: ${val(event.handling_details.feed_type)}`}
                                    size="small" 
                                    variant="outlined"
                                    color="primary"
                                  />
                                )}
                                {typeof event.handling_details?.quantity_kg !== 'undefined' && (
                                  <Chip 
                                    label={`${translateKey('quantity_kg')}: ${val(event.handling_details.quantity_kg)} kg`}
                                    size="small" 
                                    variant="outlined"
                                  />
                                )}
                                {typeof event.cost !== 'undefined' && (
                                  <Chip 
                                    label={`${translateKey('cost')}: ${val(event.cost)}`}
                                    size="small" 
                                    variant="outlined"
                                  />
                                )}
                                {event.performed_by && (
                                  <Chip 
                                    label={`${translateKey('performed_by')}: ${event.performed_by}`}
                                    size="small" 
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              {event.notes && (
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                  {translateKey('notes')}: {event.notes}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {/* Detalhes específicos de Reprodução (Cio, Secagem, Monta, Diagnóstico) */}
                          {(['monta_natural', 'diagnostico_gestacao', 'inseminacao', 'parto'].includes(event.handling_type_code) || 
                            (event.handling_type_code === 'manejos' && ['cio', 'secagem'].includes(event.title?.toLowerCase() || ''))) && (
                            <Box sx={{
                              mt: 2,
                              p: 2,
                              bgcolor: 'secondary.50',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'secondary.200'
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                               <Box
                                 sx={{
                                   width: 20,
                                   height: 20,
                                   bgcolor: 'secondary.main',
                                   maskImage: 'url(/gestores/reproducao.svg)',
                                   WebkitMaskImage: 'url(/gestores/reproducao.svg)',
                                   maskRepeat: 'no-repeat',
                                   WebkitMaskRepeat: 'no-repeat',
                                   maskSize: 'contain',
                                   WebkitMaskSize: 'contain',
                                   maskPosition: 'center',
                                   WebkitMaskPosition: 'center',
                                 }}
                               />
                               <Typography variant="body1" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                                  {event.title ? formatTitle(event.title) : translateHandlingType(event.handling_type_code)}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                {/* Monta Natural */}
                                {event.handling_details?.touro_id && (
                                  <Chip 
                                    label={`${translateKey('bull')}: ${event.handling_details.touro_id}`}
                                    size="small" 
                                    variant="outlined"
                                    color="secondary"
                                  />
                                )}

                                {/* Cio */}
                                {event.handling_details?.status && (
                                  <Chip 
                                    label={`${translateKey('status')}: ${formatTitle(event.handling_details.status)}`}
                                    size="small" 
                                    variant="outlined"
                                    color="secondary"
                                  />
                                )}

                                {/* Secagem */}
                                {event.handling_details?.status_pos && (
                                  <Chip 
                                    label={`${translateKey('status_pos')}: ${formatTitle(event.handling_details.status_pos)}`}
                                    size="small" 
                                    variant="outlined"
                                    color="secondary"
                                  />
                                )}

                                {/* Diagnóstico Gestação */}
                                {event.handling_details?.resultado_diagnostico && (
                                  <Chip 
                                    label={`${translateKey('result')}: ${formatTitle(event.handling_details.resultado_diagnostico)}`}
                                    size="small" 
                                    variant="outlined"
                                    color={event.handling_details.resultado_diagnostico.toLowerCase().includes('positivo') ? 'success' : 'default'}
                                  />
                                )}
                                {event.handling_details?.metodo_diagnostico && (
                                  <Chip 
                                    label={`${translateKey('method')}: ${formatTitle(event.handling_details.metodo_diagnostico)}`}
                                    size="small" 
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              
                              {event.notes && (
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                  {translateKey('notes')}: {event.notes}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {/* Detalhes específicos do teste de mastite com Accordion */}
                          {event.handling_type_code === 'laboratory_exam' && event.handling_details?.exam_type === 'mastitis_test' && event.handling_details?.results && (
                            <Box sx={{ mt: 2 }}>
                              <Accordion 
                                sx={{
                                  bgcolor: 'error.50',
                                  border: '1px solid',
                                  borderColor: 'error.200',
                                  borderRadius: 1,
                                  '&:before': {
                                    display: 'none',
                                  },
                                  boxShadow: 'none',
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMore sx={{ color: 'error.main' }} />}
                                  sx={{
                                    bgcolor: 'error.50',
                                    '& .MuiAccordionSummary-content': {
                                      alignItems: 'center',
                                    },
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    <LocalHospital sx={{ color: 'error.main', fontSize: 20 }} />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'error.main' }}>
                                        Teste de Mastite
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          {event.handling_date}
                                          {event.handling_time && ` às ${event.handling_time}`}
                                        </Typography>
                                        {(event.handling_details.results.exam_result || event.handling_details.results.result) && (
                                          <Chip 
                                            label={event.handling_details.results.exam_result || event.handling_details.results.result}
                                            size="small"
                                            color={getSeverityColor(getResultSeverity(event.handling_details.results.exam_result || event.handling_details.results.result || ''))}
                                            variant="filled"
                                            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                          />
                                        )}
                                      </Box>
                                    </Box>
                                  </Box>
                                </AccordionSummary>
                                
                                <AccordionDetails sx={{ bgcolor: 'background.paper', pt: 2 }}>
                                  {/* Resultado detalhado */}
                                  {(event.handling_details.results.exam_result || event.handling_details.results.result) && (
                                    <Box sx={{ mb: 3 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Resultado do Exame:
                                      </Typography>
                                      <Chip 
                                        label={event.handling_details.results.exam_result || event.handling_details.results.result}
                                        color={getSeverityColor(getResultSeverity(event.handling_details.results.exam_result || event.handling_details.results.result || ''))}
                                        variant="filled"
                                        sx={{ fontWeight: 600 }}
                                      />
                                    </Box>
                                  )}

                                  {/* Tetos afetados */}
                                  {event.handling_details.results.teatsAffected && event.handling_details.results.teatsAffected.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Tetos Afetados:
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {event.handling_details.results.teatsAffected.map((teat: string, index: number) => (
                                          <Chip key={index} label={teat} size="small" variant="outlined" color="error" />
                                        ))}
                </Box>
                                    </Box>
                                  )}

                                  {/* Aspecto do leite */}
                                  {event.handling_details.results.milkAspect && event.handling_details.results.milkAspect.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Aspecto do Leite:
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {event.handling_details.results.milkAspect.map((aspect: string, index: number) => (
                                          <Chip key={index} label={aspect} size="small" variant="outlined" color="warning" />
                                        ))}
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Sinais do úbere */}
                                  {event.handling_details.results.udderSigns && event.handling_details.results.udderSigns.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Sinais do Úbere:
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {event.handling_details.results.udderSigns.map((sign: string, index: number) => (
                                          <Chip key={index} label={sign} size="small" variant="outlined" color="warning" />
                                        ))}
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Resultados microbiológicos detalhados */}
                                  {(event.handling_details.results.staphPrimary || event.handling_details.results.strepPrimary || event.handling_details.results.generalPrimary) && (
                                    <Box sx={{ mb: 3 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Resultados Microbiológicos:
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {event.handling_details.results.staphPrimary && (
                                          <Typography variant="body2">
                                            • <strong>Staphylococcus (Primário):</strong> {event.handling_details.results.staphPrimary}
                                          </Typography>
                                        )}
                                        {event.handling_details.results.staphSecondary && (
                                          <Typography variant="body2">
                                            • <strong>Staphylococcus (Secundário):</strong> {event.handling_details.results.staphSecondary}
                                          </Typography>
                                        )}
                                        {event.handling_details.results.strepPrimary && (
                                          <Typography variant="body2">
                                            • <strong>Streptococcus (Primário):</strong> {event.handling_details.results.strepPrimary}
                                          </Typography>
                                        )}
                                        {event.handling_details.results.strepSecondary && (
                                          <Typography variant="body2">
                                            • <strong>Streptococcus (Secundário):</strong> {event.handling_details.results.strepSecondary}
                                          </Typography>
                                        )}
                                        {event.handling_details.results.generalPrimary && (
                                          <Typography variant="body2">
                                            • <strong>Geral (Primário):</strong> {event.handling_details.results.generalPrimary}
                                          </Typography>
                                        )}
                                        {event.handling_details.results.generalSecondary && (
                                          <Typography variant="body2">
                                            • <strong>Geral (Secundário):</strong> {event.handling_details.results.generalSecondary}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Tratamento Recomendado */}
                                  {event.handling_details.treatment && (
                                    <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LocalHospital fontSize="small" />
                                        Tratamento Recomendado
                                      </Typography>
                                      
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {event.handling_details.treatment.situation && (
                                          <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                              Situação:
                                            </Typography>
                                            <Typography variant="body2">
                                              {event.handling_details.treatment.situation}
                                            </Typography>
                                          </Box>
                                        )}
                                        
                                        {event.handling_details.treatment.mainTreatment && (
                                          <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                              Tratamento Principal:
                                            </Typography>
                                            <Typography variant="body2">
                                              {event.handling_details.treatment.mainTreatment}
                                            </Typography>
                                          </Box>
                                        )}
                                        
                                        {event.handling_details.treatment.supportiveTreatment && (
                                          <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                              Tratamento de Suporte:
                                            </Typography>
                                            <Typography variant="body2">
                                              {event.handling_details.treatment.supportiveTreatment}
                                            </Typography>
                                          </Box>
                                        )}
                                        
                                        {event.handling_details.treatment.recommendedAntibiotics && (
                                          <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                              Antibióticos Recomendados:
                                            </Typography>
                                            <Typography variant="body2">
                                              {event.handling_details.treatment.recommendedAntibiotics}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Observações */}
                                  {event.handling_details.results.observation && (
                                    <Box sx={{ mb: 3 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Observações:
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                        {event.handling_details.results.observation}
                                      </Typography>
                                    </Box>
                                  )}

                                  {/* Datas e informações adicionais */}
                                  <Box sx={{ 
                                    display: 'flex', 
                                    gap: 3, 
                                    flexWrap: 'wrap', 
                                    pt: 2, 
                                    borderTop: '1px solid', 
                                    borderColor: 'divider' 
                                  }}>
                                    {event.handling_details.collection_date && (
                                      <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                          Data da Coleta:
                                        </Typography>
                                        <Typography variant="body2">
                                          {formatDateStrict(event.handling_details.collection_date)}
                                        </Typography>
                                      </Box>
                                    )}
                                    {event.handling_details.exam_date && (
                                      <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                          Data do Exame:
                                        </Typography>
                                        <Typography variant="body2">
                                          {formatDateStrict(event.handling_details.exam_date)}
                                        </Typography>
                                      </Box>
                                    )}
                                    {event.handling_details.plate_id && (
                                      <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                          ID da Placa:
                                        </Typography>
                                        <Typography variant="body2">
                                          {event.handling_details.plate_id}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            </Box>
                          )}


                        </Paper>
                      </Box>
                    </Box>
                  ))})()}
                </Box>

                {/* Paginação */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(_, page) => setCurrentPage(page)}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
      </CardContent>
        </Card>
    {/* Modal de visualização de biometria */}
    <Dialog open={biometricOpen} onClose={handleCloseBiometric} maxWidth="sm" fullWidth>
      <DialogTitle>Biometria do animal</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          {biometricLoading ? (
            <Typography variant="body2">Carregando imagem...</Typography>
          ) : biometricUrl ? (
            <Box
              component="img"
              src={biometricUrl}
              alt="Biometria do focinho"
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                borderRadius: 1,
                border: '1px solid',
                borderColor: DESIGN_TOKENS.colors.neutral[200],
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">Imagem não disponível</Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
    </Box>
  );
};
